import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { AddReceivingLogResponse } from '../models/logs/AddReceivingLogResponse';
import { OperationStatus } from '../models/OperationStatus.interface';



/**
 * ToastService handles the display of toast messages in the application.
 * It provides methods to show warning and success messages using the Ionic ToastController.
 * Each toast has a duration and is positioned at the top of the screen by default.
 */


@Injectable({
  providedIn: 'root',
})
export class ToastService {

  private _toastController: ToastController;
  private toastCount = 0; // Keeps track of the active toasts, used for CSS class names

  constructor(toastController: ToastController){
    this._toastController = toastController;
  }



  /**
   * Shows a warning message as a toast notification.
   * The toast will appear at the top of the screen with a warning color.
   * 
   * @param message The message to be displayed in the toast.
   * @param duration The duration for which the toast will be shown (default is 3000ms).
   */
    async showWarningMessage(message: string, duration: number=3000): Promise<void> {
      const toastCount = this.toastCount+1;// Increment count for unique toast ID
      this.toastCount++

      const toast = await this._toastController.create({
        message: message,
        duration: duration,
        position: 'top', // Position at the top of the screen
        color: 'warning', //Set color to 'warning'
             cssClass: `toast-${toastCount}`  // Dynamically set CSS class for each toast
     
      });
      await toast.present();

          //  When toast is dismissed, decrement the counter to keep track of active toasts
    toast.onDidDismiss().then(() => {
      this.toastCount--;
    });
  }
    

  /**
   * Shows a success message as a toast notification.
   * The toast will appear at the top of the screen with a success color.
   * 
   * @param message The message to be displayed in the toast.
   * @param duration The duration for which the toast will be shown (default is 3000ms).
   */
        async showSuccessMessage(message: string, duration: number=3000): Promise<void> {
          const toastCount = this.toastCount+1; // Increment count for unique toast ID
          this.toastCount++
          const toast = await this._toastController.create({
            message: message,
            duration: duration,
            position: 'top', // Position at the top of the screen
            color: 'success', // Set color to 'success'
                 cssClass: `toast-${toastCount}`  // Dynamically set CSS class for each toast
          });
          await toast.present();

             // When toast is dismissed, decrement the counter to keep track of active toasts

    toast.onDidDismiss().then(() => {
      this.toastCount--;
    });
        }

        

        
  /**
   * Shows a sticky toast with one or more action buttons (e.g. "Retry", "Email IT")
   * plus a Dismiss button. Stays until the user acts or dismisses (no auto-timeout),
   * so an important prompt isn't missed. Each action's handler runs, then the toast
   * closes.
   *
   * @param message The message to display.
   * @param actions The action buttons, each with a label and handler.
   * @param color The toast color (default 'warning').
   */
  async showActionMessage(
    message: string,
    actions: { text: string; handler: () => void }[],
    color: string = 'warning'
  ): Promise<void> {
    const toastCount = this.toastCount + 1;
    this.toastCount++;
    const toast = await this._toastController.create({
      message,
      position: 'top',
      color,
      cssClass: `toast-${toastCount}`,
      buttons: [
        ...actions.map((action) => ({ text: action.text, handler: () => { action.handler(); } })),
        { text: 'Dismiss', role: 'cancel' },
      ],
    });
    await toast.present();
    toast.onDidDismiss().then(() => {
      this.toastCount--;
    });
  }

  /**
   * Handle an operation status by showing the appropriate toast
   * @param status The operation status to handle
   * @param operationName The name of the operation for context
   */
  public async handleOperationStatus(
    status: OperationStatus, 
    operationName: string
  ): Promise<void> {
    if (!status) {
      return;
    }

    // Don't show notifications for operations that weren't attempted
    if (status.statusCode === '0' && status.message.includes('not processed')) {
      return;
    }

    if (status.isSuccess) {
      // For "no photos to upload" or "email not requested", don't show a toast
      if (
        (operationName === 'Photo upload' && status.message === 'No photos to upload') ||
        (operationName === 'Email' && status.message === 'Email not requested')
      ) {
        return;
      }
      

    await this.showSuccessMessage(status.message);
      // Check if the message contains a note and show it longer
   if (status.userNote) {
    await this.showWarningMessage(status.userNote, 6000);
  }
  
    } else {
      const errorMessage = status.errorDetails 
        ? `${status.message}: ${status.errorDetails}`
        : status.message;
      
      await this.showWarningMessage(`${operationName} failed: ${errorMessage}`);
    }
  }

  /**
   * Process the API response from log submission and show appropriate toasts
   * @param response The API response
   */
  async processLogResponse(response: AddReceivingLogResponse): Promise<void> {
    if (!response) {
      await this.showWarningMessage('No response received from server.');
      return;
    }

    // Always show receiving log status result first (most critical)
    await this.handleOperationStatus(response.receivingLogStatus, 'Log entry');


    // These additional operation status updates were removed per Work Package 9080
    // await this.handleOperationStatus(response.receivingNoteStatus, 'PO note update');
    // await this.handleOperationStatus(response.photoUploadStatus, 'Photo upload');
    // await this.handleOperationStatus(response.emailStatus, 'Email');
  }
}
